/**
 * ADR-0106 — Models catalog at /app/workspace/models. Detail/editor lives at
 * /app/build/models/:id. Models are produced by Model Train nodes in operations
 * (no "create" button here — author one on an operation canvas).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PageLayout } from "../components/PageLayout.js";
import { ErrorState, LoadingState } from "../components/States.js";
import { useToast } from "../components/Toast.js";
import { useConfirm } from "../components/ConfirmDialog.js";
import { apiFetch } from "../lib/api.js";

type ModelListItem = {
  id: string;
  name: string;
  slug: string;
  algorithm_family: string;
  algorithm: string;
  scope: "personal" | "team" | "shared";
  champion_version: number | null;
  key_metric: { label: string; value: number } | null;
  last_trained_at: string | null;
  source_operation_id: string | null;
  source_node_id: string | null;
  source_operation_name: string | null;
};

type FamilyFilter = "all" | "regression" | "classification" | "anomaly" | "clustering";

function fmtTs(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, {
    year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit",
  });
}

function familyLabel(f: string): string {
  return f.charAt(0).toUpperCase() + f.slice(1);
}

export function WorkspaceModels() {
  const toast = useToast();
  const confirm = useConfirm();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<ModelListItem[]>([]);
  const [q, setQ] = useState("");
  const [familyFilter, setFamilyFilter] = useState<FamilyFilter>("all");

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch<{ models: ModelListItem[] }>("/api/tenant/models");
      setRows(data.models ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); }, []);

  async function onDelete(m: ModelListItem) {
    const ok = await confirm.confirm({
      title: "Delete this model?",
      detail: `This deletes "${m.name}", all its versions, and the trained artifacts. This cannot be undone.`,
      confirmLabel: "Delete",
    });
    if (!ok) return;
    try {
      await apiFetch(`/api/tenant/models/${encodeURIComponent(m.id)}`, { method: "DELETE" });
      toast.push({ kind: "success", message: `Deleted ${m.name}` });
      void load();
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    }
  }

  const filtered = rows.filter((r) => {
    if (familyFilter !== "all" && r.algorithm_family !== familyFilter) return false;
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      return r.name.toLowerCase().includes(needle) || r.slug.toLowerCase().includes(needle);
    }
    return true;
  });

  return (
    <PageLayout title="Models">
      <div style={{ display: "grid", gap: 12 }}>
        <div className="toolbar">
          <div className="toolbarGroup">
            <input
              className="input"
              style={{ width: 260 }}
              placeholder="Search models…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <span className="muted" style={{ fontSize: 12 }}>Family</span>
            <select className="select" value={familyFilter} onChange={(e) => setFamilyFilter(e.target.value as FamilyFilter)}>
              <option value="all">All</option>
              <option value="classification">Classification</option>
              <option value="regression">Regression</option>
              <option value="anomaly">Anomaly detection</option>
              <option value="clustering">Clustering</option>
            </select>
          </div>
        </div>
        <div className="muted" style={{ fontSize: 12 }}>
          Models are produced by Model Train nodes in operations — add one to an operation to create a model.
        </div>
        {error ? <ErrorState detail={error} /> : null}
        {loading ? (
          <LoadingState />
        ) : filtered.length === 0 ? (
          <div className="muted" style={{ padding: "24px 12px" }}>
            {rows.length === 0
              ? "No models yet. Add a Model Train node to an operation to create one."
              : "No models match your filters."}
          </div>
        ) : (
          <div className="tableWrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Source</th>
                  <th>Family</th>
                  <th>Champion</th>
                  <th>Key metric</th>
                  <th>Last trained</th>
                  <th>Scope</th>
                  <th style={{ width: 40 }} aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {filtered.map((m) => (
                  <tr key={m.id}>
                    <td>
                      <Link to={`/app/build/models/${encodeURIComponent(m.id)}`}>{m.name}</Link>
                      <div className="mono muted" style={{ fontSize: 11 }}>{m.slug}</div>
                    </td>
                    <td>
                      {m.source_operation_id ? (
                        <Link
                          className="linkBtn"
                          to={`/app/build/operations/${encodeURIComponent(m.source_operation_id)}${m.source_node_id ? `?node=${encodeURIComponent(m.source_node_id)}` : ""}`}
                        >
                          {m.source_operation_name ?? "Open operation"}
                        </Link>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                    <td>{familyLabel(m.algorithm_family)}</td>
                    <td>{m.champion_version != null ? `v${m.champion_version}` : "—"}</td>
                    <td>{m.key_metric ? `${m.key_metric.label} ${m.key_metric.value.toFixed(3)}` : "—"}</td>
                    <td className="muted">{fmtTs(m.last_trained_at)}</td>
                    <td><span className="pill">{m.scope}</span></td>
                    <td style={{ textAlign: "right" }}>
                      <button className="adminListAction isDanger" type="button" onClick={() => void onDelete(m)}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </PageLayout>
  );
}
